import { useEffect, useRef } from 'react';
import confetti from 'canvas-confetti';
import { IRankChange } from '../types';

/**
 * Fire a confetti burst from the left and right sides of the screen.
 * Exported so it can be triggered manually (e.g. from a test button).
 */
export const fireConfettiBurst = (): void => {
  const burst = (angle: number, origin: { x: number; y: number }): void => {
    confetti({
      particleCount: 80,
      angle,
      spread: 60,
      origin,
      colors: ['#ffd700', '#ffb800', '#ff8c00', '#ffffff', '#00d4ff'],
      ticks: 200,
      gravity: 1.2,
      scalar: 1.1,
      drift: 0,
    });
  };

  burst(60, { x: 0.15, y: 0.6 });
  burst(120, { x: 0.85, y: 0.6 });
};

interface UseConfettiCelebrationOptions {
  /** Whether confetti celebrations are enabled */
  enabled?: boolean;
  /** Minimum ms between confetti bursts to prevent rapid-fire */
  cooldownMs?: number;
}

/**
 * Hook that fires a confetti burst when a new athlete overtakes the #1 spot.
 *
 * @param rankChanges - Map of athlete ID to rank change info from useRankChanges
 * @param leaderAthleteId - The current #1 athlete's ID
 * @param options - Configuration options
 */
export const useConfettiCelebration = (
  rankChanges: Map<string, IRankChange>,
  leaderAthleteId: string | undefined,
  options: UseConfettiCelebrationOptions = {}
): void => {
  const { enabled = true, cooldownMs = 3000 } = options;

  const lastFiredRef = useRef<number>(0);
  const initialLoadRef = useRef<boolean>(true);

  useEffect(() => {
    if (!enabled) return;

    // Skip the very first render — we don't want confetti on initial page load
    if (initialLoadRef.current) {
      if (rankChanges.size > 0) {
        initialLoadRef.current = false;
      }
      return;
    }

    if (!leaderAthleteId || rankChanges.size === 0) return;

    const leaderChange = rankChanges.get(leaderAthleteId);
    if (!leaderChange) return;

    // Fire only when someone new takes #1 (moved up or is new to the board)
    const tookTheTopSpot =
      leaderChange.currentRank === 1 &&
      leaderChange.previousRank !== 1 &&
      (leaderChange.direction === 'up' || leaderChange.direction === 'new');

    if (!tookTheTopSpot) return;

    // Enforce cooldown
    const now = Date.now();
    if (now - lastFiredRef.current < cooldownMs) return;
    lastFiredRef.current = now;

    fireConfettiBurst();
  }, [rankChanges, leaderAthleteId, enabled, cooldownMs]);
};
